import { AuditEvent } from '@medplum/fhirtypes'
import dayjs from 'dayjs'
import { extractTitleAndIcon } from '@/pages/Patient/components/ChronologicalRecords.utils'
import { ForwardRefExoticComponent, ElementType } from 'react'
import { IconProps } from '@tabler/icons-react'

/**
 * Shared types and utilities for timeline grouping hooks
 */

export type ResourceTypeCount = {
  resourceType: string
  dataKey: string
  count: number
  icon: ForwardRefExoticComponent<IconProps> | ElementType
  color: string
  items: AuditEvent[]
}

// Map dataKey to display labels
export const LABEL_MAP: Record<string, string> = {
  vitals: 'Vitals',
  diagnosis: 'Diagnoses',
  medications: 'Meds',
  'labs-imaging': 'Labs',
  encounters: 'Encounters',
  procedures: 'Procedures',
  immunizations: 'Vaccines',
  allergies: 'Allergies',
  documents: 'Docs',
  'social-history': 'Social',
  'family-history': 'Family',
  orders: 'Orders',
  insurances: 'Insurance',
  'progress-notes': 'Notes',
  'history-physical': 'H&P',
  consents: 'Consents',
}

export function getResourceTypeLabel(dataKey: string): string {
  return LABEL_MAP[dataKey] || dataKey
}

/**
 * Group AuditEvents by date (YYYY-MM-DD)
 */
export function groupAuditEventsByDate(resources: AuditEvent[]): Map<string, AuditEvent[]> {
  const dateMap = new Map<string, AuditEvent[]>()

  for (const resource of resources) {
    if (!resource.recorded) continue
    const dateKey = dayjs(resource.recorded).format('YYYY-MM-DD')
    const existing = dateMap.get(dateKey) || []
    existing.push(resource)
    dateMap.set(dateKey, existing)
  }

  return dateMap
}

/**
 * Build resource type counts from a list of AuditEvents
 */
export function buildResourceCounts(items: AuditEvent[]): ResourceTypeCount[] {
  const typeMap = new Map<string, AuditEvent[]>()

  for (const event of items) {
    const linkedResource = event.entity?.[0]?.what?.resource
    const { item } = extractTitleAndIcon(linkedResource)
    if (!item) continue
    const dataKey = item?.dataKey || 'unknown'

    const existing = typeMap.get(dataKey) || []
    existing.push(event)
    typeMap.set(dataKey, existing)
  }

  const resourceCounts: ResourceTypeCount[] = []

  for (const [dataKey, events] of typeMap) {
    const sampleResource = events[0]?.entity?.[0]?.what?.resource
    const { icon, color } = extractTitleAndIcon(sampleResource)

    resourceCounts.push({
      resourceType: sampleResource?.resourceType || 'Unknown',
      dataKey,
      count: events.length,
      icon,
      color,
      items: events,
    })
  }

  resourceCounts.sort((a, b) => b.count - a.count)
  return resourceCounts
}

/**
 * Extract patient ID from AuditEvent's meta.compartment
 */
export function getPatientIdFromAuditEvent(event: AuditEvent): string | null {
  const compartment = event.meta?.compartment as { reference: string }[] | undefined
  if (!compartment) return null
  const patientRef = compartment.find((c) => c.reference?.includes('Patient'))?.reference
  return patientRef ? patientRef.replace('Patient/', '') : null
}
